import { Vector4, NormalBlending } from "three";
import RectangleMesh from "../../instancing/Meshes/RectangleMesh";
import ParticleMesh from "../../instancing/Meshes/ParticleMesh";
import RectangleOutlineMesh from "../../instancing/Meshes/RectOutlineMesh";
import getMaterialWithUniforms from "../../instancing/materials/getMaterialWithUniforms";
import { pathogenicityFiltersCNV } from "../../constants/pathogenicity";
import { PATHOGENICITY_CL } from "../../constants/options";
import createTandemDupTexture from "../../instancing/textures/createTandemDupTexture";
import createTriangleTexture from "../../instancing/textures/createTriangleTexture";
import createBreakendTexture from "../../instancing/textures/createBreakendTexture";
import createInversionTexture from "../../instancing/textures/createInversionTexture";
import ColorScale from "../../models/ColorScale";
import { GRAY_TONE } from "../../constants/colors";

const pointSize = 8;
const colors = Array(10).fill(new Vector4(0, 0, 0, 0));
const triangleText = createTriangleTexture({ size: pointSize });

export function getSVDuplicationMesh() {
  const conf = {
    rgbaCode: 1,
    raycastInd: 1,
    posX: 1,
    posY: 1,
  };
  const materialConf = getMaterialWithUniforms({})
    .setPaddingX([-1, 1, -1, 1])
    .setPaddingY([-4, -4, 4, 4])
    .setOffset([0, 0])
    .setColorMap(colors);

  return new RectangleMesh(materialConf, conf);
}

export function getBreakendMesh() {
  const { texture, uv } = createBreakendTexture({
    width: 128,
    height: 16,
  });

  const conf = {
    rgbaCode: 1,
    raycastInd: 1,
    posX: 1,
    posY: 1,
  };
  const matConf = {
    ...texture,
    antialias: true,
    transparent: true,
    blending: NormalBlending,
  };
  const materialConf = getMaterialWithUniforms(matConf)
    .setPaddingX([-1, 1, -1, 1])
    .setPaddingY([-4, -4, 4, 4])
    .setOffset([0, 0])
    .setColorMap(colors)
    .setTexture(texture.texture)
    .setTextureUvMap(uv);

  return new RectangleMesh(materialConf, conf);
}

export function getInversionMesh() {
  const { texture, uv } = createInversionTexture({
    width: 128,
    height: 16,
  });

  const conf = {
    rgbaCode: 1,
    raycastInd: 1,
    posX: 1,
    posY: 1,
  };
  const matConf = {
    ...texture,
    antialias: true,
    transparent: true,
    blending: NormalBlending,
  };
  const materialConf = getMaterialWithUniforms(matConf)
    .setPaddingX([-1, 1, -1, 1])
    .setPaddingY([-4, -4, 4, 4])
    .setOffset([0, 0])
    .setColorMap(colors)
    .setTexture(texture.texture)
    .setTextureUvMap(uv);

  return new RectangleMesh(materialConf, conf);
}

export function getRepExpMesh() {
  const { texture, uv } = createTandemDupTexture({
    width: 128,
    height: 16,
  });
  const conf = {
    rgbaCode: 1,
    raycastInd: 1,
    posX: 1,
    posY: 1,
  };
  const matConf = {
    ...texture,
    antialias: true,
    transparent: true,
    blending: NormalBlending,
  };
  const materialConf = getMaterialWithUniforms(matConf)
    .setPaddingX([-1, 1, -1, 1])
    .setPaddingY([-4, -4, 4, 4])
    .setOffset([0, 0])
    .setColorMap(colors)
    .setTexture(texture.texture)
    .setTextureUvMap(uv);

  return new RectangleMesh(materialConf, conf);
}

export function getTandemDupMesh() {
  const { texture, uv } = createTandemDupTexture({
    width: 128,
    height: 16,
  });

  const conf = {
    rgbaCode: 1,
    raycastInd: 1,
    posX: 1,
    posY: 1,
  };
  const matConf = {
    ...texture,
    antialias: true,
    transparent: true,
    blending: NormalBlending,
  };
  const materialConf = getMaterialWithUniforms(matConf)
    .setPaddingX([-1, 1, -1, 1])
    .setPaddingY([-4, -4, 4, 4])
    .setOffset([0, 0])
    .setColorMap(colors)
    .setTexture(texture.texture)
    .setTextureUvMap(uv);

  return new RectangleMesh(materialConf, conf);
}

export function getInsertionMesh() {
  const conf = {
    rgbaCode: 1,
    posX: 1,
    posY: 1,
  };
  const materialConf = getMaterialWithUniforms({
    alphaTest: 0.5,
    transparent: true,
    depthWrite: false,
    depthTest: false,
  })
    .setOffset([0, 0])
    .setColorMap(colors)
    .setPointSize(pointSize)
    .setTexture(triangleText.texture)
    .setTextureCoordMap(triangleText.textureCoordsMap);

  return new ParticleMesh(materialConf, conf);
}

export function getSVDeletionMesh() {
  const conf = {
    rgbaCode: 1,
    raycastInd: 1,
    posX: 1,
    posY: 1,
  };
  const materialConf = getMaterialWithUniforms({})
    .setPaddingX([-1, 1, -1, 1])
    .setPaddingY([-4, -4, 4, 4])
    .setOffset([0, 0])
    .setBorderWidth(1)
    .setRaycastBorderWidth(5)
    .setColorMap(colors);

  return new RectangleOutlineMesh(materialConf, conf);
}

export function updateVis(items, mesh) {
  const setY = mesh.updateY({ count: items.length, isAppend: false });
  for (let i = 0; i < items.length; i++) {
    const y = items[i].level;
    setY(i, y, y, y, y);
  }
}

export function drawVariant(items, mesh) {
  const count = items.length;
  const isAppend = true;
  const setX = mesh.updateX({ count, isAppend });
  const setY = mesh.updateY({ count, isAppend });
  const setRgb = mesh.updateRgba({ count, isAppend });
  for (let i = 0; i < count; i++) {
    const cnv = items[i];
    const y = cnv.level;
    const { g0, g1 } = cnv.region;
    const x0 = g0;
    const x1 = g1;
    const rgb = cnv.pathoIndex;
    setX(i, x0, x1, x0, x1);
    setY(i, y, y, y, y);
    setRgb(i, rgb, rgb, rgb, rgb);
  }
}

export function getColorMap(conf = {}) {
  const { isDark } = conf;
  const scaleName = conf[PATHOGENICITY_CL];
  const theme = isDark ? 2 : 1;
  const colorScale = new ColorScale(scaleName, 5).setTheme(theme);
  const unknownScale = new ColorScale(GRAY_TONE, 5).setTheme(theme);
  const allColors = colorScale.colors.concat(unknownScale.colors);

  return pathogenicityFiltersCNV
    .map((_, ind) => {
      const color = ind > 5 ? allColors[7] : allColors[ind];
      return { key: ind, color, ind, category: "pathogenicity" };
    })
    .reduce((a, i) => a.set(i.key, i), new Map());
}
